import { v4 as uuidv4 } from 'uuid';
import { Delivery, Solution } from '../../common/types/application/opportunity';
import { AcquiringChannel, Address, Application, Catalogue } from '../../common';
import { getAcquiringChannel } from '../../common/types/application/acquiring-channel';
import { getCatalogueItemFromCatalogue, isVirtualDevice } from '../../common/helpers/get-device-from-catalogue';
import { AppDelivery } from 'src/features/common/types/application/delivery';
import { Collection } from 'src/features/common/types/application/collection';
import { CartItem } from 'src/features/common/types/application/cart';
import { DeliveryOptions } from 'src/features/common/types/application/delivery-options';

export function getSolutionMapFromSolutionArray(
  solutions: Solution[],
): Map<ProductKey, ProductDetails> {
  return solutions.reduce((result, solution, index) => {
    const key = getProductKey(solution.productId, solution.acquiringChannel);

    const currentQuantity = result.get(key)?.quantity || 0;
    const productDetailsNew = {
      quantity: currentQuantity + solution.quantity,
      orderIndex: index,
    } as ProductDetails;
    result.set(key, productDetailsNew);

    return result;
  }, new Map<ProductKey, ProductDetails>());
}

export function getSolutionArrayFromMap(solutions: Map<ProductKey, ProductDetails>): Solution[] {
  return Array.from(solutions.keys())
    .reduce((result: SolutionWithIndex[], key) => {
      const productDetails = solutions.get(key);
      if (productDetails) {
        const keyProperties = getProductKeyProperties(key);
        result.push({
          productId: keyProperties.productId,
          acquiringChannel: keyProperties.acquiringChannel,
          quantity: productDetails.quantity || 0,
          orderIndex: productDetails.orderIndex,
        });
      }
      return result;
    }, [])
    .sort((solutionOne, solutionTwo) => solutionOne.orderIndex - solutionTwo.orderIndex)
    .map(
      (solutionWithIndex) =>
      ({
        productId: solutionWithIndex.productId,
        acquiringChannel: solutionWithIndex.acquiringChannel,
        quantity: solutionWithIndex.quantity,
      } as Solution),
    );
}

export function getSubtractedSolutions(
  solutionsStart: Solution[],
  solutionsToSubtract: Solution[],
): Solution[] {
  const solutionsStartMap = getSolutionMapFromSolutionArray(solutionsStart);
  const solutionsToSubtractMap = getSolutionMapFromSolutionArray(solutionsToSubtract);

  const solutionsStartMapNew = Array.from(solutionsToSubtractMap.keys()).reduce<
    Map<ProductKey, ProductDetails>
  >((result, productKey: ProductKey) => {
    const solutionToSubtract = solutionsToSubtractMap.get(productKey);
    const solutionStart = solutionsStartMap.get(productKey);
    if (solutionToSubtract) {
      const newQuantity = (solutionStart?.quantity || 0) - solutionToSubtract.quantity;
      result.set(productKey, {
        quantity: newQuantity,
        orderIndex: solutionToSubtract.orderIndex,
      });
    }

    return result;
  }, new Map<ProductKey, ProductDetails>());

  return getSolutionArrayFromMap(solutionsStartMapNew);
}

export type DeliveryBreakdown = {
  /* list of all solutions */
  solutions: Solution[];

  /* subset of all solutions: only those that are linked to addresses */
  solutionsWithAddresses: Solution[];

  /* subset of all solutions: only those that have no associated addresses */
  solutionsWithoutAddresses: Solution[];

  /* Solutions linked to addresses that are not a subset of all solutions */
  solutionsWithTooManyAddresses: Solution[];
};

/**
 *  Returns a useful breakdown of the addresses, by aggregating device quantities
 *  in the application (or catalogue if useSolutionsFromCatalogue is set to true),
 *  and delivery address details in the application.
 *
 *  @param application
 *  @param catalogue
 *  @param useSolutionsFromCatalogue - optional - when set to false, this
 *  function will use the device quantities already present in the application.
 *  When set to true, the device quantities in the catalogue will be used.
 *  Defaults to false.
 *
 * @returns DeliveryBreakdown
 */
export function getSolutionsDeliveryBreakdown(
  application: Application,
  catalogue: Catalogue,
  useSolutionsFromCatalogue: boolean = false,
): DeliveryBreakdown {

  const solutionsArray = useSolutionsFromCatalogue
    ? catalogue.products.map<Solution>((product) => {
      return {
        productId: product.device.id,
        acquiringChannel: product.device.acquiringChannel,
        quantity: product.device.quantity,
        serialNumbers: product.device.serialNumbers,
      } as Solution;
    })
    : (application.opportunity?.solutions || []).filter((solution) => {
      const catalogueItem = getCatalogueItemFromCatalogue(
        catalogue,
        solution.productId,
        solution.acquiringChannel,
      );
      return catalogueItem && !catalogueItem.device.isProductCategory;
    });
  const solutions = getSolutionMapFromSolutionArray(solutionsArray);

  const solutionsWithAddressesArray = (application?.opportunity?.deliveries || []).flatMap(
    (delivery) => delivery.solutions,
  );

  const solutionsWithAddresses = getSolutionMapFromSolutionArray(solutionsWithAddressesArray);

  const solutionTotalsMinusDeliveryTotals = Array.from(solutionsWithAddresses.keys()).reduce(
    (result, key, index) => {
      const totalQuantityRemaining = result.get(key)?.quantity || 0;
      const quantityToSubtract = solutionsWithAddresses.get(key)?.quantity || 0;

      const productDetailsNew = {
        quantity: totalQuantityRemaining - quantityToSubtract,
        orderIndex: index,
      };

      result.set(key, productDetailsNew);
      return result;
    },
    new Map<ProductKey, ProductDetails>(solutions.entries()),
  );

  const solutionsWithoutAddressesArray = getSolutionArrayFromMap(
    solutionTotalsMinusDeliveryTotals,
  ).filter((solution) => solution.quantity > 0);

  const solutionsWithTooManyAddresses = getSolutionArrayFromMap(
    solutionTotalsMinusDeliveryTotals,
  ).filter((solution) => solution.quantity < 0);

  return {
    solutions: solutionsArray,
    solutionsWithAddresses: solutionsWithAddressesArray,
    solutionsWithoutAddresses: solutionsWithoutAddressesArray,
    solutionsWithTooManyAddresses: solutionsWithTooManyAddresses,
  };
}

export function getProductKey(productId: string, acquiringChannel: AcquiringChannel): ProductKey {
  return `${productId}\\n${acquiringChannel}`;
}

export function getProductKeyProperties(
  productKey: ProductKey,
): { productId: string; acquiringChannel: AcquiringChannel } {
  const productKeyProperties = productKey.split('\\n');

  return {
    productId: productKeyProperties[0],
    acquiringChannel: getAcquiringChannel(productKeyProperties[1]),
  };
}

export function findAddress(address: Address, deliveries: Delivery[]) {
  const addressLines = [
    address?.street || '',
    address?.city || '',
    address?.province || '',
    address?.postalCode || '',
  ];

  return deliveries.find((del) => {
    const lines = [
      del.address?.street || '',
      del.address?.city || '',
      del.address?.province || '',
      del.address?.postalCode || '',
    ];

    return lines.join() === addressLines.join();
  });
}

export type ProductKey = string;

export type ProductDetails = {
  quantity: number;
  orderIndex: number;
};

export type SolutionWithIndex = Solution & {
  orderIndex: number;
};

export type AddressListItem = {
  addressLines: string[];
  assignedDevices?: boolean;
  onDeleteButtonClicked: () => void;
  onEditButtonClicked: () => void;
};

export const updateDeviceDeliveryAddress = (
  deliveries: AppDelivery[],
  collections: Collection[],
  deviceId: string,
  newAddressId: string,
  quantity: number,
  currentDeliveryId?: string
): { deliveries: AppDelivery[]; collections?: Collection[] } => {
  // Create a copy of deliveries array to avoid mutation
  const newDeliveries = [...(deliveries || [])];
  const newCollections = [...(collections || [])];

  // Find the index of the device in the deliveries array
  const currentDeviceIndex = currentDeliveryId ? newDeliveries.findIndex(
    (device) => device.productId === deviceId && device.id === currentDeliveryId
  ) : newDeliveries.findIndex(
    (device) => device.productId === deviceId
  )

  // If device not found, return the original deliveries array
  if (currentDeviceIndex === -1) {
    // No device found with the given deviceId
    // Return the original deliveries array

    // Find the device in the collections array
    const collectionDeviceIndex = newCollections.findIndex(
      (device) => device.productId === deviceId
    );

    // If the device is found in collections
    if (collectionDeviceIndex !== -1) {
      // Create a new delivery with the device from collections
      const newDelivery: AppDelivery = {
        ...newCollections[collectionDeviceIndex],
        addressId: newAddressId,
        quantity: quantity,
      };

      // Add the new delivery to the deliveries array
      newDeliveries.push(newDelivery);

      // Decrease the quantity in the collections array
      newCollections[collectionDeviceIndex].quantity -= quantity;

      // If the quantity in the collections array is less than or equal to zero
      if (newCollections[collectionDeviceIndex].quantity <= 0) {
        // Remove the device from the collections array
        newCollections.splice(collectionDeviceIndex, 1);
      }
    }

    return { deliveries: newDeliveries, collections: newCollections };
  }

  // If the addressId matches the current addressId
  if (newAddressId === newDeliveries[currentDeviceIndex].addressId) {
    // if quantity is the same we return the deliveries without changing it
    if (quantity === newDeliveries[currentDeviceIndex].quantity) return { deliveries };
    // 1. If the quantity is less than the current device quantity,
    //    we set the quantity to the new quantity and move the remaining quantity to the default
    if (quantity < newDeliveries[currentDeviceIndex].quantity) {
      const remainingQuantity = newDeliveries[currentDeviceIndex].quantity - quantity;
      newDeliveries[currentDeviceIndex].quantity = quantity;

      // Find the item in the deliveries array based on productId and addressId is 'default'
      const defaultDeliveryIndex = newDeliveries.findIndex(
        (delivery) => delivery.productId === deviceId && delivery.addressId === 'default',
      );

      // If a default delivery is found, add the remaining quantity to it
      if (defaultDeliveryIndex !== -1) {
        newDeliveries[defaultDeliveryIndex].quantity += remainingQuantity;
      } else {
        // If a default delivery is not found, create a new delivery object with the default addressId
        const defaultDelivery: AppDelivery = {
          id: uuidv4(),
          productId: deviceId,
          addressId: 'default',
          quantity: remainingQuantity,
        };
        // Add the new delivery object to the deliveries array
        newDeliveries.push(defaultDelivery);
      }
    }

    // If the quantity is greater than the current device quantity,
    // set the quantity to the new quantity and decrement the quantity in the default address
    if (quantity > newDeliveries[currentDeviceIndex].quantity) {
      const diff = quantity - newDeliveries[currentDeviceIndex].quantity;
      newDeliveries[currentDeviceIndex].quantity = quantity;

      // Find the item in the deliveries array based on productId and addressId is 'default'
      const defaultDeliveryIndex = newDeliveries.findIndex(
        (delivery) => delivery.productId === deviceId && delivery.addressId === 'default'
      );

      // If a default delivery is found, decrement the quantity
      if (defaultDeliveryIndex !== -1) {
        if (newDeliveries[defaultDeliveryIndex].quantity >= diff) {
          newDeliveries[defaultDeliveryIndex].quantity -= diff;
        } else {
          newDeliveries[defaultDeliveryIndex].quantity = 0;
        }
      }
    }
    return { deliveries: newDeliveries };
  }

  // Get the current device from the deliveries array
  const currentDevice = newDeliveries[currentDeviceIndex];

  // If the quantity of the device matches the new quantity and the address is different,
  // update the addressId of the device to the new addressId
  if (currentDevice.quantity === quantity && currentDevice.addressId !== newAddressId) {
    // Quantity matches the new quantity and addressId is different
    // Update the addressId of the device to the new addressId

    // check if the new address exists in newDeliveries, if it does move the quantity to the new address
    const newAddressIndex = newDeliveries.findIndex(
      (device) => device.addressId === newAddressId,
    );


    if (newAddressIndex !== -1) {
      newDeliveries[newAddressIndex].quantity += quantity;
      newDeliveries[currentDeviceIndex].quantity -= quantity;

      if (newDeliveries[currentDeviceIndex].quantity < 0) {
        // Remove the old delivery object from the deliveries array
        newDeliveries.splice(currentDeviceIndex, 1);
      }
    } else {
      newDeliveries[currentDeviceIndex].addressId = newAddressId;
    }

    return { deliveries: newDeliveries };
  }

  // If the addressId are different
  if (currentDevice.addressId !== newAddressId) {
    // Subtract the quantity from the old quantity using the new quantity
    newDeliveries[currentDeviceIndex].quantity -= quantity;

    if (newDeliveries[currentDeviceIndex].quantity < 0) {
      // Remove the old delivery object from the deliveries array
      newDeliveries.splice(currentDeviceIndex, 1);
    }

    // Check if the new device already exists in deliveries
    const isNewAddressExisting = newDeliveries.findIndex(
      (device) => device.productId === deviceId && device.addressId === newAddressId,
    );

    // If the new device exists, update the quantity of the existing delivery
    if (isNewAddressExisting !== -1) {
      newDeliveries[isNewAddressExisting].quantity = quantity;
    } else {
      // Assign the device to the new address by creating a new delivery object
      const newDelivery: AppDelivery = {
        ...currentDevice,
        addressId: newAddressId,
        quantity: quantity,
        id: uuidv4()
      };

      // Add the new delivery object to the deliveries array
      newDeliveries.push(newDelivery);
    }
  }

  // Return the updated deliveries array
  return { deliveries: newDeliveries };
};

// TODO: remove unused functions
export const moveDevicesToAddress = (
  deliveries: AppDelivery[],
  currentAddressId: string,
  newAddressId: string,
  quantity: number,
): AppDelivery[] => {
  const updatedDeliveries = deliveries.map((delivery) => {
    if (delivery.addressId === currentAddressId) {
      // Move device to new address
      return {
        ...delivery,
        addressId: newAddressId,
      };
    } else if (delivery.addressId === newAddressId) {
      // Update quantity if device already exists at new address
      return {
        ...delivery,
        quantity: delivery.quantity + quantity,
      };
    }
    return delivery;
  });

  return updatedDeliveries;
};

type MoveableItem = AppDelivery | Collection;

export const moveDevicesToNewAddress = <T extends MoveableItem>(
  deliveries: T[],
  devicesToMove: T[],
  newAddressId: string,
  addressIdToDelete: string,
): T[] => {
  const updatedDeliveries = [...deliveries]; // Create a copy of the original deliveries array

  // Iterate over the devices to be moved
  devicesToMove.forEach((deviceToMove) => {
    const existingDeviceIndex = updatedDeliveries.findIndex(
      (device) => device.productId === deviceToMove.productId && device.addressId === newAddressId,
    );

    // If the device already exists in the deliveries array, update its addressId
    if (existingDeviceIndex !== -1) {
      updatedDeliveries[existingDeviceIndex].quantity += deviceToMove.quantity;
    } else {
      const deviceIndex = updatedDeliveries.findIndex(
        (device) => device.productId === deviceToMove.productId,
      );
      if (deviceIndex !== -1) {
        updatedDeliveries[deviceIndex].addressId = newAddressId;
      }
    }
  });

  return updatedDeliveries.filter((delivery) => delivery.addressId !== addressIdToDelete);
};

function getUnallocatedDevices(
  cartItems: CartItem[],
  deliveries: Collection[],
  collections: Collection[],
): CartItem[] {
  const unallocatedDevices: CartItem[] = [];

  cartItems.forEach((cartItem) => {
    const deliveryQuantity = deliveries.reduce(
      (total, delivery) =>
        delivery.productId === cartItem.productId ? total + delivery.quantity : total,
      0,
    );

    const collectionQuantity = collections.reduce(
      (total, collection) =>
        collection.productId === cartItem.productId ? total + collection.quantity : total,
      0,
    );

    const totalAllocated = deliveryQuantity + collectionQuantity;
    if (cartItem.quantity !== totalAllocated) {
      const unallocatedItem = { ...cartItem, quantity: cartItem.quantity - totalAllocated };
      unallocatedDevices.push(unallocatedItem);
    }
  });

  return unallocatedDevices;
}

function updateAllocatedDevices(
  unallocatedDevices: CartItem[],
  deliveries: Collection[],
  collections: Collection[],
): { deliveries: Collection[]; collections: Collection[] } {
  unallocatedDevices.forEach((unallocatedDevice) => {
    let remainingQuantity = -unallocatedDevice.quantity;

    collections = collections.map((collection) => {
      if (collection.productId === unallocatedDevice.productId) {
        if (collection.quantity >= remainingQuantity) {
          collection.quantity -= remainingQuantity;
          remainingQuantity = 0;
        } else {
          remainingQuantity -= collection.quantity;
          collection.quantity = 0;
        }
      }
      return collection;
    });

    if (remainingQuantity > 0) {
      deliveries = deliveries.map((delivery) => {
        if (delivery.productId === unallocatedDevice.productId) {
          if (delivery.quantity >= remainingQuantity) {
            delivery.quantity -= remainingQuantity;
            remainingQuantity = 0;
          } else {
            remainingQuantity -= delivery.quantity;
            delivery.quantity = 0;
          }
        }
        return delivery;
      });
    }
  });

  return { deliveries, collections };
}

function mergeItems(
  appItems: (AppDelivery | Collection)[],
  items: (AppDelivery | Collection)[],
): (AppDelivery | Collection)[] {
  const mergedItems = [...appItems, ...items];

  const result = mergedItems.reduce((acc, item) => {
    const existingItem = acc.find(
      (existing) =>
        existing.productId === item.productId && existing.addressId === item.addressId,
    );

    if (existingItem) {
      existingItem.quantity += item.quantity;
    } else {
      acc.push({ ...item });
    }

    return acc;
  }, [] as (AppDelivery | Collection)[]);

  return result;
}

// TODO: remove unused function
export function normalizeDeliveriesAndCollections(application: Application) {
  const devices = getUnallocatedDevices(
    application?.cart || [],
    application.deliveries || [],
    application.collections || [],
  );

  // check unallocated devices by comparing cartItems with application.collections
  const unallocatedDevices = devices.filter((cartItem) => cartItem.quantity > 0);

  // from unallocated devices, get the ones that can be delivered
  const deliverableDevices = unallocatedDevices.filter(
    (cartItem) =>
      cartItem.deliveryOptions.includes(DeliveryOptions.Deliver) &&
      cartItem.quantity &&
      !isVirtualDevice(cartItem.title),
  );

  // from unallocated devices, get the ones that can be collected
  const collectableDevices = unallocatedDevices.filter(
    (cartItem) =>
      cartItem.deliveryOptions.includes(DeliveryOptions.Stock) &&
      cartItem.quantity &&
      !isVirtualDevice(cartItem.title) &&
      cartItem.deliveryOptions.length === 1,
  );

  const deliveries: AppDelivery[] = deliverableDevices.map((device) => ({
    productId: device.productId,
    addressId: 'default',
    deliveryOption: DeliveryOptions.Deliver,
    quantity: device.quantity,
    id: uuidv4(),
  }));

  const collections: Collection[] = collectableDevices.map((device) => ({
    productId: device.productId,
    addressId: 'default',
    deliveryOption: DeliveryOptions.Stock,
    quantity: device.quantity,
    id: uuidv4(),
  }));

  const normalizedDeliveries = mergeItems(application.deliveries || [], deliveries);
  const normalizedCollections = mergeItems(application.collections || [], collections);

  return updateAllocatedDevices(
    devices.filter((device) => !isVirtualDevice(device.title)),
    normalizedDeliveries,
    normalizedCollections,
  );
}
